import { ticketInDates } from "@/components/TicketSearch";

const text = value => String(value || "");
const containsAll = (value, words) => words.every(word => value.toLowerCase().includes(word));
const containsAny = (value, words) => words.some(word => value.toLowerCase().includes(word));

// An ordinary job query must not match every ticket filed by e.g.
// maintenance.pool@example.com. Explicit email queries still find people.
const personText = (value, emailQuery) => emailQuery ? text(value)
  : text(value).replace(/[^\s<>]+@[^\s<>]+/g, "").replace(/[<>]/g, "").trim();

function excerpt(value, words) {
  const compact = value.replace(/\s+/g, " ").trim();
  const positions = words.map(word => compact.toLowerCase().indexOf(word)).filter(index => index >= 0);
  const start = Math.max(0, (positions.length ? Math.min(...positions) : 0) - 45);
  const end = Math.min(compact.length, start + 180);
  return `${start ? "…" : ""}${compact.slice(start, end)}${end < compact.length ? "…" : ""}`;
}

export function matchManagementTicket(ticket, query, {
  colonyName = () => "", assigneeName = () => "", statusLabel = () => "",
} = {}) {
  const words = text(query).trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return { matches: true, rank: 0, context: null };
  const emailQuery = words.some(word => word.includes("@"));
  const fields = [
    { label: "Title", value: text(ticket.title) },
    { label: "Details", value: text(ticket.description) },
    ...(ticket.notes || []).map(note => ({ label: "Reply", value: text(note.body) })),
    { label: "Colony", value: text(colonyName(ticket.colony_id)) },
    { label: "Created by", value: personText(ticket.created_by_name, emailQuery) },
    { label: "Created by", value: emailQuery ? text(ticket.created_by_email) : "" },
    ...(ticket.assignees || []).map(id => ({ label: "Assigned to", value: personText(assigneeName(id), emailQuery) })),
    ...(ticket.notes || []).map(note => ({ label: "Reply author", value: personText(note.author_name, emailQuery) })),
    { label: "Status", value: text(statusLabel(ticket.status)) },
  ];
  const matches = containsAll(fields.map(field => field.value).join(" "), words);
  const titleMatch = containsAll(fields[0].value, words);
  const rank = titleMatch ? 0 : containsAny(fields[0].value, words) ? 1 : 2;
  const field = matches && !titleMatch ? fields.slice(1).find(item => containsAny(item.value, words)) : null;
  return { matches, rank, context: field ? { label: field.label, text: excerpt(field.value, words) } : null };
}

export function managementTicketResults(tickets, {
  query = "", status = "all", colony = "all", from = "", to = "", sort = "newest",
  colonyName = () => "", assigneeName = () => "", statusLabel = () => "",
} = {}) {
  const matches = new Map();
  const counts = { all: 0 };
  const candidates = tickets.filter(ticket => {
    if (colony !== "all" && ticket.colony_id !== colony) return false;
    if (!ticketInDates(ticket, from, to, { includeUpdated: true })) return false;
    const match = matchManagementTicket(ticket, query, { colonyName, assigneeName, statusLabel });
    if (!match.matches) return false;
    matches.set(ticket.id, match);
    counts.all += 1;
    counts[ticket.status] = (counts[ticket.status] || 0) + 1;
    return true;
  });
  const byCreatedDesc = (a, b) => text(b.created_at).localeCompare(text(a.created_at));
  const selectedOrder = (a, b) => {
    if (sort === "oldest") return text(a.created_at).localeCompare(text(b.created_at));
    if (sort === "updated") return text(b.status_updated_at || b.created_at).localeCompare(text(a.status_updated_at || a.created_at));
    if (sort === "colony") return text(colonyName(a.colony_id)).localeCompare(text(colonyName(b.colony_id))) || byCreatedDesc(a, b);
    if (sort === "scheduled") return text(a.scheduled_for || "9999").localeCompare(text(b.scheduled_for || "9999")) || byCreatedDesc(a, b);
    return byCreatedDesc(a, b);
  };
  const pin = ticket => ticket.urgent && ["open", "in_progress"].includes(ticket.status) ? 1 : 0;
  const filtered = candidates.filter(ticket => status === "all" || ticket.status === status);
  filtered.sort((a, b) => matches.get(a.id).rank - matches.get(b.id).rank || pin(b) - pin(a) || selectedOrder(a, b));
  return { filtered, counts, matches };
}